'use client';

import React from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Building2, ImageIcon, Loader2, RotateCcw } from 'lucide-react';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import type { TrashItem } from '../model/trash.types';
import { isTrashProject } from '../model/trash.types';

interface TrashCardProps {
  item: TrashItem;
  onRestore: (item: TrashItem) => void;
  isRestoring?: boolean;
}

/**
 * One deleted building or design, with the deadline the API computed for it.
 *
 * Same geometry as the Recent grid cards (4:3 image, rounded-lg, label block
 * underneath) so the Trash reads as part of Recent and not a foreign screen.
 * The image is desaturated: this thing is gone until the user brings it back.
 * Nothing here is a link — a deleted item has no page to open.
 */
export function TrashCard({ item, onRestore, isRestoring = false }: TrashCardProps) {
  const t = useTranslations('history.trash');
  const tConcept = useTranslations('concept');

  const isProject = isTrashProject(item);
  const { name, project_sequence_number, image_url, restore_deadline_label } =
    item.attributes;

  const title = isProject
    ? name || t('untitledBuilding')
    : name
      ? tConcept('conceptNumberWithTitle', {
          number: project_sequence_number ?? 0,
          title: name,
        })
      : tConcept('conceptNumber', { number: project_sequence_number ?? 0 });

  // ONE meta line on every card, the same grammar the estimate card uses
  // ("total · design"): a design prefixes its building, a building has nothing
  // above it to name. Two lines on one kind and one on the other would leave
  // the Restore buttons in a row at different heights (and the mobile grids
  // cannot stretch a cell to its row at all). The deadline itself is composed
  // and localized by the API.
  const parentName = isProject ? null : item.relationships.project?.meta.name;
  // Deadline first: on a two-column phone grid the line truncates, and the part
  // worth keeping is how long is left, not which building the design belonged to.
  const metaLine = parentName
    ? `${restore_deadline_label} · ${parentName}`
    : restore_deadline_label;

  return (
    <div>
      <div className="relative aspect-[4/3] overflow-hidden rounded-lg shadow-sm ring-1 ring-border/50 bg-muted">
        {image_url ? (
          <Image
            src={image_url}
            alt={title}
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, 25vw"
            className="object-cover saturate-[0.25] brightness-95 opacity-60"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            {isProject ? (
              <Building2 className="h-8 w-8" />
            ) : (
              <ImageIcon className="h-8 w-8" />
            )}
          </div>
        )}

        {/* Type chip: the feed mixes buildings and designs, and restoring a
            building brings its designs back with it, so the difference has to
            be readable at a glance. */}
        <Badge className="absolute top-2 start-2 z-20 text-chip bg-muted/90 text-muted-foreground hover:bg-muted/90">
          {isProject ? t('typeBuilding') : t('typeDesign')}
        </Badge>
      </div>

      <div className="px-2 pt-2">
        <p className="text-list-item-value truncate">{title}</p>
        <p className="text-list-item-meta text-muted-foreground truncate">
          {metaLine}
        </p>
      </div>

      <Button
        variant="outline"
        size="sm"
        className="mt-2 w-full gap-1.5"
        onClick={() => onRestore(item)}
        disabled={isRestoring}
      >
        {isRestoring ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <RotateCcw className="h-4 w-4 rtl:-scale-x-100" />
        )}
        {isRestoring ? t('restoring') : t('restore')}
      </Button>
    </div>
  );
}
